import React, { useEffect, useState } from 'react';
import './AnalyseOffreFinanciere.css';

interface Article {
  id: string;
  code: string;
  nom: string;
  prix: number;
}

interface Section {
  id: string;
  name: string;
  articles: Article[];
}

interface Prestataire {
  id: string;
  id_status: number;
  organisme: string;
  nom_entite: string;
  sections: Section[];
  totalHT: number;
  updated_at: string;
  note_financiere: number;
}

interface AnalyseOffreFinanciereProps {
  subsequentId: string;
}

const AnalyseOffreFinanciere: React.FC<AnalyseOffreFinanciereProps> = ({ subsequentId }) => {
  const [prestataires, setPrestataires] = useState<Prestataire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operation, setOperation] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${subsequentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Erreur API');
        const data = await response.json();

        if (data.status !== 'success') throw new Error('Réponse API invalide');

        const commandesData = data.data.commandes || [];
        const operation = data.data || [];
        const prestatairesFormatted = commandesData.map((commande: any, index: number) => {
          let totalHT = 0;

          const sections = (commande.sections || []).map((section: any) => ({
            id: String(section.id),
            name: section.name || '',
            articles: (section.articles || []).map((article: any) => {
              const prix = article.prix || 0;
              totalHT += prix;
              return {
                id: String(article.id),
                code: String(article.code),
                description: article.description,
                nom: article.nom || '',
                prix
              };
            })
          }));
          const date = new Date(commande.updated_at || new Date());
          const dateReponse = `${date.toLocaleDateString('fr-FR')} à ${date.getHours().toString().padStart(2, '0')}h${date.getMinutes().toString().padStart(2, '0')}`;

          return {
            id: String(commande.id),
            id_status: Number(commande.id_status),
            organisme: `Entreprise ${String.fromCharCode(65 + index)}`,
            nom_entite: commande.contrat.entite.nom,
            sections,
            totalHT,
            updated_at: dateReponse,
            note_financiere: commande.note_financiere,
          };
        });
        setOperation(operation);
        setPrestataires(prestatairesFormatted);
        console.log('Prestatairessss:', prestataires);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subsequentId]);

  const calculateNoteFinanciere = (prestaId: string): number => {
    const montants = operation?.commandes
      ?.filter(p => p.prix_total !== undefined && p.id_status !== 12) // ignore les expirées
      .map(p => ({ id: p.id, montant: p.prix_total })) || [];

    if (!montants.length) return 0; // aucune offre valide

    const currentMontant = montants.find(m => m.id.toString() === prestaId.toString())?.montant;

    if (currentMontant === undefined) return 0; // prestataire expiré

    const minMontant = Math.min(...montants.map(m => m.montant));
    const maxMontant = Math.max(...montants.map(m => m.montant));

    if (montants.length === 1 || minMontant === maxMontant) return 10;

    const note = ((maxMontant - currentMontant) / (maxMontant - minMontant)) * 10;
    return Math.round(note * 100) / 100;
  };
  const InfoIcon = () => (
    <svg className="info-icon" viewBox="0 0 16 16" fill="#6B7280">
      <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 12C7.4 12 7 11.6 7 11C7 10.4 7.4 10 8 10C8.6 10 9 10.4 9 11C9 11.6 8.6 12 8 12ZM9 9H7V5H9V9Z" />
    </svg>
  );

  const QuestionIcon = () => (
    <svg
      className="question-icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Cercle gris */}
      <circle cx="12" cy="12" r="12" fill="#6B7280" />

      {/* Point d'interrogation blanc */}
      <text
        x="12"
        y="20"
        textAnchor="middle"
        fontSize="20"
        fontFamily="Arial, sans-serif"
        fill="#FFFFFF"
        fontWeight="bold"
      >
        ?
      </text>
    </svg>
  );

  if (loading) return <div className="loading">Chargement...</div>;
  if (error) return <div className="error">Erreur: {error}</div>;
  if (prestataires.length === 0) {
    return <div className="no-data">Aucune offre financière disponible pour cette opération.</div>;
  }

  const getCandidatureDate = (commandeId) => {
    const entry = operation?.historique?.find(
      h => h.prefix === 'Candidature' && h.destinataire?.toString() === commandeId?.toString()
    );
    return entry ? entry.created_at : null;
  };

  return (
    <div className={`of-comparatif-container ${!loading ? 'analyse-financiere-loaded' : ''}`}>
      <div className="presta-comparaison-grid">
        {prestataires.map((presta) => {
          const candidatureDate = getCandidatureDate(presta.id);
          const isExpired = presta.id_status === 12;
          const isRefused = presta.id_status === 6;
          const isAccepted = presta.id_status === 11;
          const isPending = [7, 8, 9, 10].includes(presta.id_status);


          return (
            <div
              className={`presta-col ${isExpired || isRefused ? "disabled" : ""} ${isPending || isAccepted ? "accepted" : ""}`}
              key={presta.id}
            >
              {/* Badges selon statut */}
              {isExpired && <div className="expired-badge">Expirée</div>}
              {isRefused && <div className="expired-badge">Refusée</div>}
              {!isExpired && !isRefused && isPending && (
                <div className="best-note-badge">Signature en cours</div>
              )}
              {!isExpired && !isRefused && isAccepted && (
                <div className="best-note-badge">Acceptée</div>
              )}

              <div className="presta-header">
                <h3>{presta.nom_entite}</h3>
                {isExpired ? (
                  <span>Offre non reçue</span>
                ) : (
                  <span>
                    Offre reçue le {
                      candidatureDate
                        ? `${new Date(candidatureDate).toLocaleDateString('fr-FR')} à ${new Date(candidatureDate).getHours().toString().padStart(2, '0')}h${new Date(candidatureDate).getMinutes().toString().padStart(2, '0')}`
                        : '-'
                    }
                  </span>
                )
                }
              </div>

              <table className="articles-table">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th></th>
                    <th>Prix HT</th>
                  </tr>
                </thead>
                <tbody className='sections-container'>
                  {presta.sections.map(section => (
                    <React.Fragment key={section.id}>
                      <div className='section-item'>
                        <tr>
                          <td colSpan={3} className="table-section-header">{section.name}</td>
                        </tr>
                        {section.articles.map(article => (
                          <tr key={article.id} className='section-body'>
                            <td colSpan={1}>
                              <div className="article-numero">
                                <span>{article.code}</span>
                                <div className="info-icon-container">
                                  <QuestionIcon />
                                  <div className="tooltip">
                                    {article.nom}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {/* <td colSpan={3}>
                              <span
                                className="article-nom"
                                title={article.nom} // Tooltip natif pour l'accessibilité
                              >
                                {article.nom && article.nom.length > 40
                                  ? `${article.nom.substring(0, 40)}...`
                                  : article.nom || ''
                                }
                              </span>
                            </td> */}
                            {isExpired ? (
                              <td colSpan={2}>-</td>
                            ) : (
                              <td colSpan={2}>{article.prix.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                            )}
                          </tr>
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div className="total-row">
                Total DPGF HT:&nbsp;
                {isExpired ? (
                  "-"
                ) : (
                  <>
                    {presta.totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </>
                )}
              </div>

              <div className="note-financiere">
                <h4>Note financière</h4>
                <div className="note-value">

                  {isExpired
                    ? "-"
                    : `${(
                      presta.note_financiere ??
                      calculateNoteFinanciere(presta.id)
                    ).toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} / 10`}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
};

export default AnalyseOffreFinanciere;